import { ChangeDetectionStrategy, Component, ErrorHandler, OnDestroy, OnInit, QueryList, ViewChildren, signal } from '@angular/core'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { applyEach, disabled, FieldTree, form, required } from '@angular/forms/signals'
import { MatInput } from '@angular/material/input'

import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { IFirestoreBatch } from 'app/_jaettu-angular/base-firebase.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { BehaviorSubject, Observable, Subject, combineLatest, of as observableOf } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

interface ProjektiLomake {
  avain: string
  nimi: string
  vari: string | null
  usableInLemonator: boolean
  viewableInLemonator: boolean
  viewableInLemonaid: boolean
  aktiivinen: boolean
  raporteilla: boolean
}

interface ProjektitLomake {
  projektitArray: ProjektiLomake[]
}

@Component({
  selector: '[app-kirjanpito-tilintarkastus-projektit-listaus]',
  templateUrl: './tilintarkastus-projektit-listaus.component.html',
  styleUrls: ['./tilintarkastus-projektit-listaus.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoTilintarkastusProjektitListausComponent implements OnInit, OnDestroy {
  @ViewChildren('nimi', { read: MatInput }) nimiInputit: QueryList<MatInput>

  private _addedProjektitSubject: BehaviorSubject<ProjektiLomake[]> = new BehaviorSubject([])
  projektitArrayObservable: Observable<FieldTree<ProjektiLomake>[]>

  lemonaidProjektitUriObservable: Observable<string>
  lemonaidProjektitEncodedUriObservable: Observable<string>
  lemonatorProjektitUriObservable: Observable<string>
  lemonatorProjektitEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  private _ngUnsubscribe = new Subject<void>()

  projektiLomake = form(signal<ProjektitLomake>({ projektitArray: [] }), polku => {
    applyEach(polku.projektitArray, projekti => {
      required(projekti.nimi)
      required(projekti.vari)
      disabled(projekti)
    })
  })

  colorNameAndClassMap: {}
  namename: string = 'afasdfasdfayuiopålkjhgf' + new Date().getTime()

  private _saveStarted: boolean
  showCommonError: boolean = false

  constructor(
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoService: KirjanpitoService,
    private _validationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _timestampService: TimestampService,
  ) {}

  ngOnInit() {
    this.colorNameAndClassMap = this._kirjanpitoService.getColorNameAndClassMap()
    const tietokannastaObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableOf<KirjanpidonProjekti[]>([])
        }
        return this._firebase.firestoreCollection<KirjanpidonProjekti>(this._kirjanpitoUriService.annaKirjanpitoProjektitCollection(asiakas.avain)).listen()
      }),
      map(projektit => {
        const formGroups: ProjektiLomake[] = []
        if (projektit?.length) {
          projektit.sort((a, b) => {
            return (a.nimi || '').localeCompare(b.nimi || '')
          })
          for (const projekti of projektit) {
            formGroups.push(this._createProjektiForm(projekti))
          }
        }
        return formGroups
      }),
    )
    this.projektitArrayObservable = combineLatest([tietokannastaObservable, this._addedProjektitSubject]).pipe(
      map(([tietokannasta, projektit]) => {
        asetaArvo(this.projektiLomake.projektitArray, [...tietokannasta, ...projektit])
        return [...this.projektiLomake.projektitArray]
      }),
    )

    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    this.lemonaidProjektitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain + '/projektit/'
        }
        return ''
      }),
    )
    this.lemonaidProjektitEncodedUriObservable = this.lemonaidProjektitUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this.lemonatorProjektitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._kirjanpitoUriService.annaKirjanpitoProjektitCollection(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.lemonatorProjektitEncodedUriObservable = this.lemonatorProjektitUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )
  }

  // createNewProject() {
  //   const newProject: KirjanpidonProjekti = {
  //     avain: this._firebase.firestoreCreateId(),
  //     nimi: null,
  //     color: null,
  //     usableInLemonator: true,
  //     viewableInLemonator: true,
  //     viewableInLemonaid: false
  //   }
  //   this._addedProjektitSubject.value.push(this._createProjektiForm(newProject))
  //   this._addedProjektitSubject.next(this._addedProjektitSubject.value)
  //   setTimeout(() => {
  //     this._ngZone.run(() => {
  //       const last = this.nimiInputit.last
  //       if (last) { last.focus() }
  //     })
  //   }, 300)
  // }

  private _createProjektiForm(projekti: KirjanpidonProjekti): ProjektiLomake {
    return {
      avain: projekti.avain,
      nimi: projekti.nimi ?? '',
      vari: projekti.color,
      usableInLemonator: projekti.usableInLemonator,
      viewableInLemonator: projekti.viewableInLemonator,
      viewableInLemonaid: projekti.viewableInLemonaid,
      aktiivinen: projekti.viewableInLemonaid,
      raporteilla: projekti.usableInLemonator,
    }
  }

  // async saveValidProjektit() {

  //   if (!this.projektiForm.valid) {
  //     this._validationService.merkitseKokoLomakeKosketuksi(this.projektiForm)
  //     return
  //   }

  //   if (this._saveStarted) {
  //     return
  //   }
  //   this._saveStarted = true
  //   this._ladataanService.aloitaLataaminen()

  //   const batch = this._firebase.firestoreBatch()
  //   const asiakastiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
  //   const projektitArray = this.projektiForm.get('projektitArray') as FormArray<FormGroup<ProjektiForm>>
  //   const kaikkiProjektit = projektitArray.controls ? projektitArray.controls : []

  //   for (const form of kaikkiProjektit) {
  //     await this.addProjektiToBatch(asiakastiedot.avain, form, batch)
  //   }

  //   // Projektien synkkaus Lemonaidiin
  //   const alikokoelmaSynkkaustyoDoc = 'tyojono/' + asiakastiedot.avain + '/alikokoelmat-lemontree-export/' + this._firebase.firestoreCreateId()
  //   const alikokoelmaSynkkaustyonTiedot: AlikokoelmaanVientiLemonatoristaLemonaidiinTyo = {
  //     aloitettu: this._timestampService.now(),
  //     tyyppi: 'projektit'
  //   }
  //   batch.set(alikokoelmaSynkkaustyoDoc, alikokoelmaSynkkaustyonTiedot)

  //   const toBePersisted = this._addedProjektitSubject.value
  //   this._addedProjektitSubject.next([])
  //   return batch.commit().then(() => {
  //     this._addedProjektitSubject.value.length = 0
  //     this._addedProjektitSubject.next(this._addedProjektitSubject.value)
  //   }).catch(error => {
  //     this._addedProjektitSubject.next(toBePersisted)
  //     this.showCommonError = true
  //     this._errorHandler.handleError(new Error('Projektit save failed ' + error?.toString()))
  //   }).finally(() => {
  //     this._saveStarted = false
  //     this._ladataanService.lopetaLataaminen()
  //   })

  // }

  private async addProjektiToBatch(asiakasAvain: string, projekti: FieldTree<ProjektiLomake>, batch: IFirestoreBatch) {
    const avain = projekti.avain().value()
    const doc = this._kirjanpitoUriService.annaKirjanpitoProjektinUri(asiakasAvain, avain)
    const data: KirjanpidonProjekti = {
      avain: avain,
      nimi: projekti.nimi().value(),
      color: projekti.vari().value(),
      usableInLemonator: projekti.usableInLemonator().value(),
      viewableInLemonator: projekti.viewableInLemonator().value(),
      viewableInLemonaid: projekti.viewableInLemonaid().value(),
    }
    batch.set(doc, data)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
